import React, { useState, useEffect } from 'react';
import { Users, Plus, Check, Shield, Star, Ear, Scale, Mic, Heart, MessageCircle, ChevronDown, ChevronUp } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';

const GOAL_ICONS = {
  shield: Shield,
  star: Star,
  ear: Ear,
  scale: Scale,
  users: Users,
  mic: Mic,
};

const AccountabilityPods = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState('');
  const [podName, setPodName] = useState('');
  const [creating, setCreating] = useState(false);
  const [checkInNote, setCheckInNote] = useState('');
  const [checkInPod, setCheckInPod] = useState(null);
  const [checkInRating, setCheckInRating] = useState(3);
  const [expandedPod, setExpandedPod] = useState(null);
  const [checkIns, setCheckIns] = useState({});

  const fetchData = async () => {
    try {
      const d = await apiClient.request('/api/self-dev/pods');
      setData(d);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    if (!selectedGoal) return;
    setCreating(true);
    try {
      await apiClient.request('/api/self-dev/pods', {
        method: 'POST',
        body: JSON.stringify({ goal: selectedGoal, name: podName }),
      });
      setShowCreate(false);
      setSelectedGoal('');
      setPodName('');
      fetchData();
    } catch {}
    setCreating(false);
  };

  const handleJoin = async (podId) => {
    try {
      await apiClient.request(`/api/self-dev/pods/${podId}/join`, { method: 'POST' });
      fetchData();
    } catch {}
  };

  const handleCheckIn = async () => {
    if (!checkInPod) return;
    try {
      await apiClient.request(`/api/self-dev/pods/${checkInPod}/check-in`, {
        method: 'POST',
        body: JSON.stringify({ note: checkInNote, progress_rating: checkInRating }),
      });
      setCheckInPod(null);
      setCheckInNote('');
      setCheckInRating(3);
      fetchData();
    } catch {}
  };

  const handleExpand = async (podId) => {
    if (expandedPod === podId) {
      setExpandedPod(null);
      return;
    }
    setExpandedPod(podId);
    if (!checkIns[podId]) {
      try {
        const d = await apiClient.request(`/api/self-dev/pods/${podId}/check-ins`);
        setCheckIns(prev => ({ ...prev, [podId]: d.check_ins }));
      } catch {}
    }
  };

  if (loading) return <DashboardLayout><div className="p-8 text-center text-gray-400">Loading pods...</div></DashboardLayout>;
  if (!data) return <DashboardLayout><div className="p-8 text-center text-gray-400">Could not load pods</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-4xl mx-auto" data-testid="accountability-pods-page">
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 flex items-center justify-center gap-2">
            <Users size={28} className="text-indigo-600" />
            Accountability Pods
          </h1>
          <p className="text-sm text-gray-500 mt-1">Small groups, big growth — check in weekly with your pod</p>
        </div>

        {/* My Pods */}
        {data.my_pods.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">My Pods</h2>
            <div className="space-y-3">
              {data.my_pods.map(pod => {
                const GoalIcon = GOAL_ICONS[pod.goal_icon] || Star;
                const isExpanded = expandedPod === pod.pod_id;
                return (
                  <div key={pod.pod_id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid={`my-pod-${pod.pod_id}`}>
                    <div className="p-4 sm:p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${pod.goal_color}15` }}>
                            <GoalIcon size={20} style={{ color: pod.goal_color }} />
                          </div>
                          <div>
                            <h3 className="text-sm font-bold text-gray-900">{pod.name}</h3>
                            <p className="text-[10px] text-gray-400">{pod.goal_label} • {pod.members.length}/{pod.max_members} members</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${pod.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {pod.status === 'active' ? 'Active' : 'Recruiting'}
                        </span>
                      </div>

                      {/* Members */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {pod.members.map((m, i) => (
                          <div key={i} className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-full text-xs">
                            <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-600">
                              {m.name.charAt(0)}
                            </div>
                            <span className="text-gray-700 font-medium">{m.name.split(' ')[0]}</span>
                            <span className="text-gray-400">{m.check_ins}ci</span>
                          </div>
                        ))}
                      </div>

                      <div className="flex gap-2">
                        <button onClick={() => setCheckInPod(pod.pod_id)} className="flex-1 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl text-sm" data-testid={`checkin-pod-${pod.pod_id}`}>
                          Weekly Check-In
                        </button>
                        <button onClick={() => handleExpand(pod.pod_id)} className="px-3 py-2 border border-gray-300 rounded-xl text-gray-500">
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                      </div>
                    </div>

                    {isExpanded && checkIns[pod.pod_id] && (
                      <div className="border-t border-gray-100 p-4 bg-gray-50">
                        <p className="text-xs font-bold text-gray-500 mb-2">Recent Check-Ins</p>
                        {checkIns[pod.pod_id].length > 0 ? (
                          <div className="space-y-2">
                            {checkIns[pod.pod_id].slice(0, 10).map((ci, i) => (
                              <div key={i} className="p-2 bg-white rounded-lg text-xs">
                                <span className="font-semibold text-gray-900">{ci.user_name}</span>
                                <span className="text-gray-400 ml-1">({ci.progress_rating}/5)</span>
                                {ci.note && <p className="text-gray-600 mt-0.5">{ci.note}</p>}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400">No check-ins yet</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Check-In Modal */}
        {checkInPod && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm" data-testid="pod-checkin-modal">
              <h3 className="font-bold text-gray-900 mb-3 text-center">Weekly Check-In</h3>
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-500 mb-1.5">How&apos;s your progress? (1-5)</p>
                <div className="flex gap-2 justify-center">
                  {[1, 2, 3, 4, 5].map(r => (
                    <button key={r} onClick={() => setCheckInRating(r)} className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${r <= checkInRating ? 'bg-indigo-500 text-white scale-105' : 'bg-gray-100 text-gray-400'}`} data-testid={`checkin-rating-${r}`}>
                      <Star size={16} fill={r <= checkInRating ? 'white' : 'none'} />
                    </button>
                  ))}
                </div>
              </div>
              <textarea value={checkInNote} onChange={e => setCheckInNote(e.target.value)} placeholder="Share your wins and struggles this week..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3" rows={3} data-testid="checkin-note-input" />
              <div className="flex gap-2">
                <button onClick={() => setCheckInPod(null)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button>
                <button onClick={handleCheckIn} className="flex-1 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-lg text-sm" data-testid="submit-checkin-btn">Check In (+10 hearts)</button>
              </div>
            </div>
          </div>
        )}

        {/* Available Pods */}
        {data.available_pods.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Join a Pod</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.available_pods.map(pod => {
                const GoalIcon = GOAL_ICONS[pod.goal_icon] || Star;
                return (
                  <div key={pod.pod_id} className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid={`available-pod-${pod.pod_id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <GoalIcon size={16} style={{ color: pod.goal_color }} />
                      <span className="text-sm font-bold text-gray-900">{pod.name}</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">{pod.goal_label} • {pod.members.length}/{pod.max_members} members</p>
                    <button onClick={() => handleJoin(pod.pod_id)} className="w-full py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-lg text-sm" data-testid={`join-pod-${pod.pod_id}`}>Join Pod</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Create Pod */}
        <div className="text-center">
          <button onClick={() => setShowCreate(!showCreate)} className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all flex items-center gap-2 mx-auto" data-testid="create-pod-btn">
            <Plus size={16} /> Create a Pod
          </button>
        </div>

        {showCreate && (
          <div className="mt-4 p-5 bg-white rounded-2xl border-2 border-indigo-200 shadow-sm max-w-md mx-auto" data-testid="create-pod-panel">
            <h3 className="font-bold text-gray-900 mb-3">Create Your Pod</h3>
            <div className="mb-3">
              <label className="text-xs font-medium text-gray-700 mb-1.5 block">Choose a Goal</label>
              <div className="grid grid-cols-2 gap-2">
                {data.goals.map(g => {
                  const GoalIcon = GOAL_ICONS[g.icon] || Star;
                  return (
                    <button key={g.key} onClick={() => setSelectedGoal(g.key)} className={`p-2 rounded-lg border-2 text-left transition-all text-xs ${selectedGoal === g.key ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`} data-testid={`goal-${g.key}`}>
                      <GoalIcon size={14} style={{ color: g.color }} className="mb-1" />
                      <span className="font-medium text-gray-700">{g.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <input type="text" value={podName} onChange={e => setPodName(e.target.value)} placeholder="Pod name (optional)" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3" data-testid="pod-name-input" />
            <div className="flex gap-2">
              <button onClick={() => setShowCreate(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button>
              <button onClick={handleCreate} disabled={!selectedGoal || creating} className="flex-1 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="submit-pod-btn">{creating ? 'Creating...' : 'Create Pod'}</button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AccountabilityPods;
